import { c as _c } from "react/compiler-runtime";
import { ActionIcon, Tooltip, Button } from '@mantine/core';
import { Fragment, ReactNode, useEffect, useState } from 'react';
import { HiCheckCircle } from 'react-icons/hi';
import { RichText } from 'components/form/RichText';
import { isNullOrEmpty, notNullOrEmpty } from 'shared/collection';
import { Expandable, LabelEmptyList, Markdown } from 'shared/ui';
import { match, P } from 'ts-pattern';
import { MdEdit } from 'react-icons/md';
import { usePortal } from 'components/portal/portal';
import { DbFormSubset, dbFormSubset } from 'components/hooks/useDbForm';
import { AutoQ as A } from 'api/autoQ';
import { UIState, useUIItem } from 'components/hooks/useUIState';
import { useDebounceMutation } from 'components/hooks/useDebounceMutation';
import { AutoConfigureCard } from './QuestionNew';
import { okOrThrow } from 'shared/promise';
import { QuestionTitleInput, QuestionSubtitleInput } from './QuestionForm';
import { VoteButtons, type VoteButtonsProps } from './VoteButtons';
import type { SubqCfg, SubqStatus, SubqType } from 'shared/forecast/subq';
import { ForecastTypeIcon, SubqTypeIcon } from './Subqs';
import { QuestionTileItems } from './QuestionTile';
import { QuestionStatusIcon } from './QuestionStatus';
import type { QuestionStatus } from 'shared/forecast/questionStatus';
import { questionStatusMd } from 'shared/forecast/questionStatus';
import { subqStatusMd } from './subq';
import type { QuestionForecastType } from 'shared/forecast';
import { isEmpty } from 'remeda';
export type QuestionForTitleForm = Pick<A.GetTitleSuggestionsRequest['question'], 'title' | 'subTitle' | 'description'>;
type GetTitleSuggestionProps = {
  options?: Omit<A.GetTitleSuggestionsRequest['options'], 'workspace_id'>;
  question: QuestionForTitleForm;
};
const zSuggestionForm = A.zTitleSuggestions.extend({
  priorRequest: A.client.md.getTitleSuggestions.zReq.shape.question
}).describe('title-suggestions');
export const useQuestionTitleForm = t0 => {
  const $ = _c(26);
  const {
    readonly,
    qForm: qFormRaw,
    typeLabel,
    uiState,
    parent
  } = t0;
  let t1;
  if ($[0] !== uiState) {
    t1 = {
      state: uiState,
      schema: zSuggestionForm
    };
    $[0] = uiState;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const suggestionsForm = useUIItem(t1);
  const qForm = dbFormSubset(qFormRaw, ".", {
    onChange: t2 => {
      const {
        delta,
        cfg,
        description
      } = t2;
      const req = {
        question: {
          ...cfg,
          parent
        },
        priorRequest: suggestionsForm.values?.priorRequest
      };
      if (description != "accept" && ("title" in delta || "subTitle" in delta) && !isNullOrEmpty(cfg.title?.trim()) && (!suggestionsForm.values?.suggestions || !suggestionsForm.values.priorRequest || !A.cfgEqual(cfg, suggestionsForm.values.priorRequest, {
        keys: ["title", "subTitle"]
      }))) {
        getSuggestionMute.debouncedMutate(req);
      }
    }
  });
  const {
    values: question
  } = qForm;
  const {
    workspace_id
  } = usePortal();
  let t3;
  if ($[2] !== suggestionsForm.values?.priorRequest || $[3] !== workspace_id) {
    t3 = async (t4, signal) => {
      const {
        question: question_0,
        options
      } = t4;
      const res = await A.getTitleSuggestions({
        question: question_0,
        options: {
          workspace_id,
          ...options
        },
        priorRequest: suggestionsForm.values?.priorRequest
      }, {
        signal
      }).then(okOrThrow);
      return {
        ...res,
        priorRequest: question_0
      };
    };
    $[2] = suggestionsForm.values?.priorRequest;
    $[3] = workspace_id;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== suggestionsForm) {
    t4 = res_0 => {
      if (res_0.usePrior) {
        return;
      }
      suggestionsForm.setValues(res_0);
    };
    $[5] = suggestionsForm;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = {
      mutationFn: t3,
      onSuccess: t4
    };
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const getSuggestionMute = useDebounceMutation(t5);
  let t6;
  if ($[10] !== question?.title || $[11] !== suggestionsForm) {
    t6 = () => {
      if (suggestionsForm.values?.suggestions && isNullOrEmpty(question?.title?.trim())) {
        suggestionsForm.del();
      }
    };
    $[10] = question?.title;
    $[11] = suggestionsForm;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = question?.title;
  let t8;
  if ($[13] !== t7) {
    t8 = [t7];
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  useEffect(t6, t8);
  const [editDescription, setEditDescription] = useState(question?.description == "");
  let t9;
  if ($[15] !== suggestionsForm) {
    t9 = () => {
      suggestionsForm.del();
      setEditDescription(false);
    };
    $[15] = suggestionsForm;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const reset = t9;
  let t10;
  if ($[17] !== editDescription || $[18] !== getSuggestionMute || $[19] !== parent || $[20] !== qForm || $[21] !== readonly || $[22] !== reset || $[23] !== suggestionsForm || $[24] !== typeLabel) {
    t10 = {
      qForm,
      suggestionsForm,
      editDescription,
      setEditDescription,
      readonly,
      typeLabel,
      parent,
      getSuggestionMute,
      reset
    };
    $[17] = editDescription;
    $[18] = getSuggestionMute;
    $[19] = parent;
    $[20] = qForm;
    $[21] = readonly;
    $[22] = reset;
    $[23] = suggestionsForm;
    $[24] = typeLabel;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
};
type UseQuestionTitleForm = ReturnType<typeof useQuestionTitleForm>;

/** form to display or edit the question title, subtitle, description for editing */
export const QuestionTitleForm = ({
  uTitleForm,
  topRight,
  belowForm
}: {
  uTitleForm: UseQuestionTitleForm;
  topRight?: ReactNode;
  belowForm?: ReactNode;
}) => {
  const {
    qForm,
    suggestionsForm,
    editDescription,
    setEditDescription,
    getSuggestionMute,
    readonly,
    typeLabel,
    parent
  } = uTitleForm;
  const {
    values: question
  } = qForm;
  const inProps = (path: keyof QuestionForTitleForm) => qForm.getInputProps(path, {
    description: 'user'
  });
  return match(question).with(P.not(P.nullish), v => {
    const question = {
      ...v,
      parent
    };
    return <>
          <div className='flex items-center justify-between'>
            <QuestionTitleInput value={v.title} readonly={readonly} typeLabel={typeLabel} {...inProps('title')} />
            {topRight}
          </div>
          <QuestionSubtitleInput value={v.subTitle} readonly={readonly} {...inProps('subTitle')} />
          <Expandable className='py-1 ' hideScroll maxHeight={!editDescription ? '10em' : undefined}>
            <div className='flex'>
              {readonly || !editDescription ? <Markdown>{question.description}</Markdown> : <RichText {...inProps('description')} className='max-w-text' syncValue placeholder='Notes (optional - context and further clarifications with rich formatting, links and images)' />}
              {!readonly && !(editDescription && isNullOrEmpty(question.description)) && <Tooltip label={editDescription ? 'Finished editing' : 'Edit description'}>
                  <ActionIcon onClick={() => setEditDescription(!editDescription)}>
                    {editDescription ? <HiCheckCircle /> : <MdEdit />}
                  </ActionIcon>
                </Tooltip>}
            </div>
          </Expandable>
          {belowForm}
          {!readonly && notNullOrEmpty(question.title?.trim()) && (suggestionsForm.values?.suggestions != null || getSuggestionMute.isPending || getSuggestionMute.isError) && <AutoConfigureCard className='mt-4' color='orange' mutation={getSuggestionMute} retry={() => getSuggestionMute.mutate({
        question,
        options: {
          forceRefresh: true
        }
      })} title='Title Suggestions' onReset={() => {
        suggestionsForm.setValues({
          suggestions: undefined,
          for: question
        });
      }}>
                {notNullOrEmpty(suggestionsForm.values?.suggestions) ? <div className='grid grid-cols-[auto,1fr] gap-4'>
                    {suggestionsForm.values?.suggestions?.map(s => <Fragment key={s.property}>
                        <div className='font-semibold opacity-30 text-sm'>
                          {match(s.property).with('title', () => 'Title').with('subTitle', () => 'Sub-title').with('description', () => 'Notes').otherwise(() => s.property)}
                        </div>
                        <div>
                          {s.newValue && s.type == 'suggestion' && match(s.property).with('title', () => <div className=''>{s.newValue}</div>).with('subTitle', () => <div className='opacity-80 '>{s.newValue}</div>).with('description', () => <Markdown>{s.newValue}</Markdown>).otherwise(() => <div>{s.newValue}</div>)}
                          <div className='text-orange-700/60'>
                            {notNullOrEmpty(s.newValue) && <span className='text-md'>↑ </span>}
                            {s.comment}
                          </div>
                        </div>
                      </Fragment>)}
                  </div> : suggestionsForm.values?.suggestions != null ? <LabelEmptyList className='text-sm'>No suggestions</LabelEmptyList> : null}
                {notNullOrEmpty(suggestionsForm.values?.suggestions?.filter(s => notNullOrEmpty(s.newValue))) && <div className='flex justify-end gap-2 mt-6 mb-1 items-center'>
                    <Button variant='filled' color='orange' onClick={() => {
            suggestionsForm.setValues({
              suggestions: null,
              for: question
            });
            const newValues = Object.fromEntries(suggestionsForm.values?.suggestions?.filter(s => s.type == 'suggestion').map(s => [s.property, s.newValue]) ?? []);
            qForm.setValues(newValues, 'accept');
          }}>
                      Update with these suggestions
                    </Button>
                  </div>}
              </AutoConfigureCard>}
        </>;
  }).otherwise(() => <>what</>);
};
export const QuestionTitle = t0 => {
  const $ = _c(12);
  const {
    heightMode: t1,
    title,
    subTitle,
    description,
    vote,
    forecastType,
    created_by,
    ...props
  } = t0;
  const heightMode = t1 === undefined ? "short" : t1;
  const {
    profilesUI
  } = usePortal();
  let t2;
  if ($[0] !== title) {
    t2 = title && <h3 className="text-balance">{title}</h3>;
    $[0] = title;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== subTitle) {
    t3 = subTitle && <div className="opacity-60">{subTitle}</div>;
    $[2] = subTitle;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== description || $[5] !== heightMode) {
    t4 = description && <Expandable hideScroll={true} maxHeight={heightMode == "short" ? "2.5em" : undefined} showMoreOverlay={true}>{match(description).with(P.string, _temp).otherwise(_temp2)}</Expandable>;
    $[4] = description;
    $[5] = heightMode;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = !isEmpty(props) && <div className="flex gap-4 items-center">{props.type == "subq" && <SubqTypeIcon variant="icon" type={props.subquestionType} size="18px" />}{forecastType && <ForecastTypeIcon type={forecastType} />}{created_by && profilesUI && <QuestionTileItems profile={profilesUI[created_by]} />}{props.type == "subq" && props.status && <QuestionStatusIcon item={{
      value: props.status,
      md: subqStatusMd[props.status]
    }} />}{props.type == "question" && props.status && <QuestionStatusIcon item={{
      value: props.status,
      md: questionStatusMd[props.status]
    }} />}{vote && <VoteButtons {...vote} />}</div>;
  let t6;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4 || $[10] !== t5) {
    t6 = <>{t2}{t3}{t4}{t5}</>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};
function _temp(d) {
  return <Markdown className="break-words">{d}</Markdown>;
}
function _temp2(d_0) {
  return d_0;
}